"use client";

import useInputChange from "hooks/useInputChange";
import { FormEvent, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { createEvent } from "services/event.service";
import { IEvent, IEventCreate } from "types/event.interface";
import { User } from "types/user.type";
import { HttpError } from "types/http.type";
import { addDays } from "date-fns";
import useUsers from "hooks/useUsers";

// import useUsers from "@/hooks/queries/useUsers";

interface Props {
  onNext: () => void;
  data?: IEvent;
}
export default function EventForm({ data, onNext }: Props) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  const { state: input, onChange } = useInputChange<IEventCreate>(
    data || {
      title: ``,
      description: ``,
      startDateTime: "",
      endDateTime: "",
      userId: "",
      date: "",
      attendees: [],
    }
  );

  const [errors, setErrors] = useState({
    title: ``,
    description: ``,
    startDateTime: "",
    endDateTime: "",
    userId: "",
    date: "",
  });

  const {
    isLoading: loadingUsers,
    data: users,
    onQueryChange,
  } = useUsers({
    page: 1,
    limit: 5,
    search,
  });

  const { isLoading, mutate } = useMutation(
    async (input: IEventCreate) => await createEvent(input!),
    {
      onSuccess(data) {
        toast(data.message);
        onNext();
      },
      onError(error: HttpError) {
        toast(error?.response.data.message, { type: "error" });
      },
    }
  );

  const handleSearch = (search: string) => {
    setSearch(search);
    onQueryChange("search", search);
  };

  const getEventDates = (input: IEventCreate) => {
    const [sHr, sMin] = input.startDateTime?.split(":");
    const [eHr, eMin] = input.endDateTime?.split(":");
    const eventDate = new Date(input.date!);
    const eventStartTime = new Date(input.date!);
    const eventEndTime = new Date(input.date!);

    eventStartTime.setHours(Number(sHr));
    eventStartTime.setMinutes(Number(sMin));

    eventEndTime.setMinutes(Number(eMin));
    eventEndTime.setHours(Number(eHr));

    return {
      eventDate: eventDate.toISOString(),
      eventStartTime: eventStartTime.toISOString(),
      eventEndTime: eventEndTime.toISOString(),
    };
  };

  const submitHandler = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({
      title: "",
      description: "",
      startDateTime: "",
      endDateTime: "",
      userId: "",
      date: "",
    });

    const { eventStartTime, eventDate, eventEndTime } = getEventDates(input);
    const currentDate = new Date();
    if (currentDate > addDays(new Date(eventDate), 1)) {
      setErrors((e) => ({ ...e, date: "Enter a date in the future" }));
      return;
    }
    const startTime = new Date(eventStartTime).getTime();
    const endTime = new Date(eventEndTime).getTime();

    if (startTime > endTime) {
      setErrors((e) => ({
        ...e,
        endDateTime: "End time should be greater than start time",
      }));
      return;
    }

    mutate({
      ...input,
      startDateTime: eventStartTime,
      endDateTime: eventEndTime,
      date: eventDate,
      attendees: selected,
    });
  };

  const handleSelection = (userId: string) => {
    if (selected.includes(userId)) {
      const filtered = selected.filter((user) => user !== userId);

      setSelected(filtered);
    } else {
      setSelected(selected.concat(userId));
    }
  };
  return (
    <div className="  p-4 ">
      <form onSubmit={submitHandler} className="flex flex-col gap-4 ">
        <div className="flex flex-col gap-2">
          <label htmlFor="title">Title</label>
          <input
            value={input.title}
            required
            onChange={onChange}
            placeholder="Meeting with John"
            id="title"
            name="title"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
          />
          <span className="text-notice font-body font-normal text-xs">
            {errors.title}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="description">Description</label>
          <textarea
            value={input.description}
            required
            onChange={onChange}
            placeholder="Brief description"
            id="description"
            name="description"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
          ></textarea>
          <span className="text-notice font-body font-normal text-xs">
            {errors.description}
          </span>
        </div>
        <div className="flex flex-col gap-2 ">
          <label htmlFor="date">Start Date</label>

          <input
            value={input.date}
            required
            onChange={onChange}
            placeholder="MM/DD/YYYY"
            id="startDate"
            name="date"
            type="date"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
          />

          {errors.date && (
            <span className="text-xs text-notice font-body font-normal">
              {errors.date}
            </span>
          )}
        </div>
        <div className=" flex  gap-4 mt-4">
          <div className="flex flex-col gap-2 w-1/2">
            <label htmlFor="startDateTime">Start Time</label>
            <input
              value={input.startDateTime}
              required
              onChange={onChange}
              placeholder="MM/DD/YYYY"
              id="startDateTime"
              name="startDateTime"
              type="time"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            />

            {errors.startDateTime && (
              <span className="text-xs text-notice font-body font-normal">
                {errors.startDateTime}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2 w-1/2">
            <label htmlFor="startDateTime">End Time</label>
            <input
              value={input.endDateTime}
              required
              onChange={onChange}
              placeholder="MM/DD/YYYY"
              id="endDateTime"
              name="endDateTime"
              type="time"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            />

            {errors.endDateTime && (
              <span className="text-xs text-notice font-body font-normal">
                {errors.endDateTime}
              </span>
            )}
          </div>
        </div>

        <label htmlFor="attendees" className=" font-medium text-base">
          Add attendees
        </label>
        <input
          onChange={({ target }) => handleSearch(target.value)}
          value={search}
          placeholder="Search users..."
          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
        />
        {loadingUsers ? (
          <span>Loading... </span>
        ) : (
          <ul className="space-y-4 max-h-[500px] overflow-auto">
            {users?.data?.data?.map((user) => {
              return (
                <li
                  key={user._id}
                  className="border  rounded-sm p-4 py-2 flex flex-col lg:flex-row justify-between lg:items-center hover:shadow-md transition"
                >
                  <div>
                    <h3 className="text-base font-body font-medium text-gray-700">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-sm text-gray-500">{user.role}</p>
                    <div className="mt-2 flex gap-2"></div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSelection(user._id)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition"
                  >
                    {!selected.includes(user._id) ? "Add user" : "Remove user"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <button className="px-4 mt-8 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition">
          {isLoading ? <span>Loading...</span> : "Create Meeting"}
        </button>
      </form>
    </div>
  );
}
